"use client";

import type { ReactNode } from "react";
import { useAuth } from "@/contexts/auth-context";

interface CapabilityGateProps {
  /** A single permission key, or a list — see `mode` for how a list is combined. */
  capability: string | string[];
  /** "any" (default): render children if the user holds at least one of the
   * listed capabilities. "all": require every one. Irrelevant for a single key. */
  mode?: "any" | "all";
  /** Rendered instead of children when the check fails. Defaults to nothing —
   * most nav/button call sites just want to disappear, not show a placeholder. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * UX-layer capability gate — Phase 6 of [[rbac-permission-overhaul-initiative]].
 * Hides `children` (or renders `fallback`) when the current user's
 * `capabilities` (from `GET /profile/me`, see the `User` type in lib/api.ts)
 * don't include the required key(s).
 *
 * This is presentation only, per the plan's invariant #11 — the backend's
 * `require_permission(...)` dependency is the real enforcement. A user who
 * navigates straight to a hidden route, or calls a hidden action's endpoint
 * directly, still gets a genuine 403 there; this component only avoids
 * showing them a door that leads nowhere.
 */
export function CapabilityGate({
  capability,
  mode = "any",
  fallback = null,
  children,
}: CapabilityGateProps) {
  const { hasCapability, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <>{fallback}</>;

  const keys = Array.isArray(capability) ? capability : [capability];
  const allowed = mode === "all" ? keys.every(hasCapability) : keys.some(hasCapability);
  return <>{allowed ? children : fallback}</>;
}
